import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Modal } from 'antd'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { Button } from 'designSystem/components/Button'
import { DOCUMENT_FEEDBACK, useGeckoAnalytics } from 'features/analytics'
import { useGeckoNotifications } from 'features/notifications'
import { useState } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

export enum AssetIssueType {
  INCORRECT_LABEL = 'Incorrect Label',
  WRONG_BOUNDING_BOX = 'Wrong Bounding Box',
  INCORRECT_OFF_PAGE_CONNECTOR = 'Incorrect Off Page Connector',
  WRONG_ASSET_TYPE = 'Wrong Asset Type',
  OTHER = 'Other',
}

export enum DocumentIssueType {
  WRONG_DOCUMENT_TYPE = 'Wrong Document Type',
  WRONG_SYSTEM = 'Wrong System',
  WRONG_DRAWING_NUMBER = 'Wrong Drawing Number',
  OTHER = 'Other',
}

const ISSUE_TYPE_LABELS: Record<string, MessageDescriptor> = {
  [AssetIssueType.INCORRECT_LABEL]: msg`Incorrect Label`,
  [AssetIssueType.WRONG_BOUNDING_BOX]: msg`Wrong Bounding Box`,
  [AssetIssueType.INCORRECT_OFF_PAGE_CONNECTOR]: msg`Incorrect Off Page Connector`,
  [AssetIssueType.WRONG_ASSET_TYPE]: msg`Wrong Asset Type`,
  [AssetIssueType.OTHER]: msg`Other`,
  [DocumentIssueType.WRONG_DOCUMENT_TYPE]: msg`Wrong Document Type`,
  [DocumentIssueType.WRONG_SYSTEM]: msg`Wrong System`,
  [DocumentIssueType.WRONG_DRAWING_NUMBER]: msg`Wrong Drawing Number`,
}

type FeedbackPayload = {
  issue_type: string
  comment: string | null
  box_id: string | null
  box_label: string | null
  box_type: string | null
  document_id: string
  site_id: string
}

type BaseFeedbackModalProps = {
  isOpen: boolean
  onClose: () => void
  documentId: string
  siteId: string
  title?: string
}

type AssetFeedbackModalProps = BaseFeedbackModalProps & {
  feedbackType: 'asset'
  boxId: string
  boxLabel: string | null
  boxType: string | null
}

type DocumentFeedbackModalProps = BaseFeedbackModalProps & {
  feedbackType: 'document'
}

type FeedbackModalProps = AssetFeedbackModalProps | DocumentFeedbackModalProps

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Textarea = styled.textarea`
  width: 100%;
  min-height: 5rem;
  padding: 0.5rem;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
  ${mobileInputTextStyles}
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`

export function FeedbackModal(props: FeedbackModalProps) {
  const { t, i18n } = useLingui()
  const {
    isOpen,
    onClose,
    documentId,
    siteId,
    feedbackType,
    title = t`Report Issue`,
  } = props
  const { trackEvent } = useGeckoAnalytics()
  const { successNotification } = useGeckoNotifications()
  const [issueType, setIssueType] = useState<string | null>(null)
  const [comment, setComment] = useState('')

  const issueTypes =
    feedbackType === 'asset'
      ? Object.values(AssetIssueType)
      : Object.values(DocumentIssueType)

  const handleSubmit = () => {
    if (!issueType) return

    const payload: FeedbackPayload = {
      issue_type: issueType,
      comment: comment.trim() || null,
      box_id: props.feedbackType === 'asset' ? props.boxId : null,
      box_label: props.feedbackType === 'asset' ? props.boxLabel : null,
      box_type: props.feedbackType === 'asset' ? props.boxType : null,
      document_id: documentId,
      site_id: siteId,
    }

    trackEvent(DOCUMENT_FEEDBACK.title, payload)
    successNotification(t`Feedback sent`)
    handleClose()
  }

  const handleClose = () => {
    setIssueType(null)
    setComment('')
    onClose()
  }

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      width={400}>
      <FormGroup>
        <Label>{t`Issue Type *`}</Label>
        <Dropdown
          placeholder={t`Select an issue type`}
          value={issueType ?? undefined}
          onValueChange={(value) => setIssueType(value)}
          options={issueTypes.map((type) => ({
            label: i18n._(ISSUE_TYPE_LABELS[type]),
            value: type,
          }))}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t`Comment (optional)`}</Label>
        <Textarea
          placeholder={t`Add any additional details...`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </FormGroup>

      <ButtonRow>
        <Button variant='ghost' size='lg' onClick={handleClose}>
          {t`Cancel`}
        </Button>
        <Button
          variant='raised'
          size='lg'
          disabled={!issueType}
          onClick={handleSubmit}>
          {t`Submit`}
        </Button>
      </ButtonRow>
    </Modal>
  )
}
